import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { Select } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import { Row, Col, FormGroup, Input, Card, CardBody } from "reactstrap";

import { MUIIcons } from "../../../AbstractElements";
import LabelTooltip from "../../../CommonElements/LabelTooltip";
import { useAppDispatch, useAppSelector } from "../../../ReduxToolkit/Hooks";
import { getTrainingImpressionReport } from "../../../ReduxToolkit/Reducers/ReportSlice";
import {
  Search,
  SearchAllItems,
  Records,
  AverageShort,
  Short30D,
  Short60D,
  Short90D,
  TrainingImpressionDashboardSubtitle,
  Employee,
  Program,
  Schedule,
  Instructor,
  Feedback,
  By,
  EmployeeView,
  InstructorView,
  ProgramView,
  RMView,
  From,
  To,
  NoFeedbackGiven,
  NoTrainingImpressionDataAvailable,
  DateRangeError,
} from "../../../utils/Constant";
import { formatDate, showToast } from "../../../utils/helper/helper";
import { encrypt } from "../../../utils/helper/localStorageutils";

import "./style.scss";

const TrainingDashboard = () => {
  const dispatch = useAppDispatch();
  const { trainingImpressionReportData } = useAppSelector(
    state => state.report
  );
  const [activeView, setActiveView] = useState("employee");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Initialize date range with default values
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    return {
      start: threeMonthsAgo.toISOString().split("T")[0],
      end: today.toISOString().split("T")[0],
    };
  });
  useEffect(() => {
    dispatch(
      getTrainingImpressionReport({
        from: formatDate(dateRange.start),
        to: formatDate(dateRange.end),
      })
    );
  }, [dateRange]);

  // Transform API data to UI format
  const transformedData = useMemo(() => {
    let records = [];
    if (Array.isArray(trainingImpressionReportData)) {
      records = trainingImpressionReportData;
    } else if (
      trainingImpressionReportData?.data &&
      Array.isArray(trainingImpressionReportData.data)
    ) {
      records = trainingImpressionReportData.data;
    } else if (
      trainingImpressionReportData &&
      typeof trainingImpressionReportData === "object"
    ) {
      // If it's an object but not an array, try to extract records
      const possibleArrays = Object.values(trainingImpressionReportData).filter(
        Array.isArray
      );
      if (possibleArrays.length > 0) {
        records = possibleArrays[0];
      }
    }
    const transformed = records.map((record, index) => {
      const transformedRecord = {
        id: `${record.course_schedule_id || index}_${record.employee_id || record.employee_name || index}`,
        employeeName: record.employee_name || "-",
        employeeCode: record.employee_id || "-",
        programName: record.program_name || "-",
        schedule: `${record.schedule_start_date || "-"} ${To} ${record.schedule_end_date || "-"}`,
        scheduleStart: record.schedule_start_date
          ? new Date(record.schedule_start_date)
          : null,
        instructorName: record.instructor_name || "-",
        instructorCode: record.instructor_id || "-",
        rmName: record.current_reporting_manager_name || "-",
        rmCode: record.current_reporting_manager_id || "-",
        rating30: Number(record.rating_30_days) || 0,
        rating60: Number(record.rating_60_days) || 0,
        rating90: Number(record.rating_90_days) || 0,
        feedback30Id: record["30_days_training_feedback_id"] || "-",
        feedback60Id: record["60_days_training_feedback_id"] || "-",
        feedback90Id: record["90_days_training_feedback_id"] || "-",
        feedback30By: record["30_days_feedback_given_by_manager_name"] || "-",
        feedback60By: record["60_days_feedback_given_by_manager_name"] || "-",
        feedback90By: record["90_days_feedback_given_by_manager_name"] || "-",
      };
      return transformedRecord;
    });
    return transformed;
  }, [trainingImpressionReportData]);

  const handleFeedbackClick = feedbackId => {
    if (feedbackId && feedbackId !== "-") {
      const data = { isReportMode: true, feedbackId: feedbackId };
      const encodedData = encodeURIComponent(encrypt(data));
      window.open(`/training-impressions?data=${encodedData}`, "_blank");
    } else {
      showToast(NoFeedbackGiven, "error");
    }
  };

  const getAverage = (value: number) => {
    if (!value || value === 0) return 0;
    return Number(value.toFixed(1));
  };

  const getRatingClass = rating => {
    if (rating >= 4.5)
      return "training-inpression-dashboard__rating--excellent";
    if (rating >= 4.0) return "training-inpression-dashboard__rating--good";
    if (rating >= 3.5) return "training-inpression-dashboard__rating--average";
    return "training-inpression-dashboard__rating--poor";
  };

  const getRatingIcon = rating => {
    if (rating >= 4.5) return "🟢";
    if (rating >= 4.0) return "🔵";
    if (rating >= 3.5) return "🟡";
    return "🔴";
  };

  const filteredData = useMemo(() => {
    const filtered = transformedData.filter(item => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        Object.values(item).some(
          value =>
            value &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );

      // Date filter - Fixed the date parsing logic
      let matchesDateRange = true;
      if (dateRange.start || dateRange.end) {
        // Extract the start date from the schedule string
        const scheduleStartStr = item.schedule.split(` ${To} `)[0];

        if (scheduleStartStr && scheduleStartStr !== "-") {
          try {
            const scheduleStartDate = new Date(scheduleStartStr);
            const startDate = dateRange.start
              ? new Date(dateRange.start)
              : null;
            const endDate = dateRange.end ? new Date(dateRange.end) : null;

            matchesDateRange =
              (!startDate || scheduleStartDate >= startDate) &&
              (!endDate || scheduleStartDate <= endDate);
          } catch (error) {
            console.warn(error);
            matchesDateRange = true;
          }
        }
      }
      const matches = matchesSearch && matchesDateRange;
      return matches;
    });
    return filtered;
  }, [transformedData, searchTerm, dateRange]);

  const viewOptions = [
    {
      id: "employee",
      label: EmployeeView,
      icon: <MUIIcons size={17} iconName="PeopleOutlineOutlined" />,
      count: filteredData.length,
    },
    {
      id: "program",
      label: ProgramView,
      icon: <MUIIcons size={17} iconName="MenuBookOutlined" />,
      count: new Set(filteredData.map(d => d.programName)).size,
    },
    {
      id: "instructor",
      label: InstructorView,
      icon: <MUIIcons size={17} iconName="SupervisorAccountOutlined" />,
      count: new Set(filteredData.map(d => d.instructorName)).size,
    },
    {
      id: "rm",
      label: RMView,
      icon: <MUIIcons size={17} iconName="TrendingUpOutlined" />,
      count: new Set(filteredData.map(d => d.rmName)).size,
    },
  ];
  const toggleGroup = groupId => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const renderEmployeeView = () => {
    // Add loading state check
    if (!filteredData.length) {
      return (
        <div className="m-2">
          <Card>
            <CardBody className="m-2">
              <p className="text-center training-inpression-dashboard__header-subtitle">
                {NoTrainingImpressionDataAvailable}
              </p>
            </CardBody>
          </Card>
        </div>
      );
    }
    return (
      <div className="training-inpression-dashboard__table">
        <div className="training-inpression-dashboard__table-container">
          <table className="training-inpression-dashboard__table-element">
            <thead className="training-inpression-dashboard__table-header">
              <tr>
                <th>{Employee}</th>
                <th>{Program}</th>
                <th>{Schedule}</th>
                <th>{Instructor}</th>
                <th className="text-center">{Short30D}</th>
                <th className="text-center">{Short60D}</th>
                <th className="text-center">{Short90D}</th>
              </tr>
            </thead>
            <tbody className="training-inpression-dashboard__table-body">
              {filteredData.map(item => (
                <tr key={item.id}>
                  <td>
                    <div className="training-inpression-dashboard__table-employee">
                      <div className="training-inpression-dashboard__table-employee-info">
                        <div className="training-inpression-dashboard__table-employee-name">
                          {item.employeeName}
                        </div>
                        <div className="training-inpression-dashboard__table-employee-code">
                          {item.employeeCode}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="training-inpression-dashboard__table-program">
                      {item.programName}
                    </div>
                  </td>
                  <td>
                    <div className="training-inpression-dashboard__table-schedule">
                      {item.schedule}
                    </div>
                  </td>
                  <td>
                    <div className="training-inpression-dashboard__table-instructor">
                      {item.instructorName}
                    </div>
                    <div className="training-inpression-dashboard__table-instructor-code">
                      {item.instructorCode}
                    </div>
                  </td>
                  <td className="text-center">
                    <span
                      onClick={() => handleFeedbackClick(item.feedback30Id)}
                      className={`training-inpression-dashboard__rating ${getRatingClass(item.rating30)}`}
                      title={`${Feedback} ${By} ${item.feedback30By}`}
                    >
                      {getRatingIcon(item.rating30)} {item.rating30}
                    </span>
                  </td>
                  <td className="text-center">
                    <span
                      onClick={() => handleFeedbackClick(item.feedback60Id)}
                      className={`training-inpression-dashboard__rating ${getRatingClass(item.rating60)}`}
                      title={`${Feedback} ${By} ${item.feedback60By}`}
                    >
                      {getRatingIcon(item.rating60)} {item.rating60}
                    </span>
                  </td>
                  <td className="text-center">
                    <span
                      onClick={() => handleFeedbackClick(item.feedback90Id)}
                      className={`training-inpression-dashboard__rating ${getRatingClass(item.rating90)}`}
                      title={`${Feedback} ${By} ${item.feedback90By}`}
                    >
                      {getRatingIcon(item.rating90)} {item.rating90}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderGroupedView = (groupKey, headerClass) => {
    const grouped = filteredData.reduce((acc, item) => {
      const key =
        groupKey === "programName"
          ? item.programName
          : groupKey === "instructorName"
            ? `${item.instructorName} (${item.instructorCode})`
            : `${item.rmName} (${item.rmCode})`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    const groupKeys = Object.keys(grouped).sort();

    if (groupKeys.length === 0) {
      return (
        <div className="m-2">
          <Card>
            <CardBody className="m-2">
              <p className="text-center training-inpression-dashboard__header-subtitle">
                {NoTrainingImpressionDataAvailable}
              </p>
            </CardBody>
          </Card>
        </div>
      );
    }

    return (
      <div className="training-inpression-dashboard__groups">
        {groupKeys.map(groupName => {
          const items = grouped[groupName];
          const isExpanded = expandedGroups.has(groupName);
          const avgRating30 =
            items.reduce((sum, item) => sum + item.rating30, 0) / items.length;
          const avgRating60 =
            items.reduce((sum, item) => sum + item.rating60, 0) / items.length;
          const avgRating90 =
            items.reduce((sum, item) => sum + item.rating90, 0) / items.length;

          return (
            <div
              key={groupName}
              className="training-inpression-dashboard__group"
            >
              <div className="training-inpression-dashboard__group-card">
                <div
                  className={`training-inpression-dashboard__group-header ${headerClass}`}
                  onClick={() => toggleGroup(groupName)}
                >
                  <div className="training-inpression-dashboard__group-header-content">
                    <div className="training-inpression-dashboard__group-header-left">
                      <div className="training-inpression-dashboard__group-header-title-area">
                        {isExpanded ? (
                          <MUIIcons size={17} iconName="ExpandLessOutlined" />
                        ) : (
                          <MUIIcons size={17} iconName="ExpandMoreOutlined" />
                        )}{" "}
                        <h3 className="training-inpression-dashboard__group-header-title">
                          {groupName}
                        </h3>
                      </div>
                      <span className="training-inpression-dashboard__group-header-count">
                        {items.length} {Records}
                      </span>
                    </div>
                    <div className="training-inpression-dashboard__group-header-right">
                      <div className="training-inpression-dashboard__group-header-averages">
                        {AverageShort}
                        <span
                          className={`rating-badge ${getRatingClass(avgRating30)}`}
                        >
                          {`${Short30D}:`} {getAverage(avgRating30)}
                        </span>
                        <span
                          className={`rating-badge ${getRatingClass(avgRating60)}`}
                        >
                          {`${Short60D}:`} {getAverage(avgRating60)}
                        </span>
                        <span
                          className={`rating-badge ${getRatingClass(avgRating90)}`}
                        >
                          {`${Short90D}:`} {getAverage(avgRating90)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="training-inpression-dashboard__group-table">
                    <div className="training-inpression-dashboard__table-container">
                      <table className="training-inpression-dashboard__table-element">
                        <thead className="training-inpression-dashboard__table-header">
                          <tr>
                            {groupKey !== "programName" && <th>{Program}</th>}
                            {groupKey !== "instructorName" && (
                              <th>{Instructor}</th>
                            )}
                            <th>{Employee}</th>
                            <th>{Schedule}</th>
                            <th className="text-center">{Short30D}</th>
                            <th className="text-center">{Short60D}</th>
                            <th className="text-center">{Short90D}</th>
                          </tr>
                        </thead>
                        <tbody className="training-inpression-dashboard__table-body">
                          {items.map(item => (
                            <tr key={item.id}>
                              {groupKey !== "programName" && (
                                <td className="training-inpression-dashboard__table-program">
                                  {item.programName}
                                </td>
                              )}
                              {groupKey !== "instructorName" && (
                                <td className="training-inpression-dashboard__table-instructor">
                                  {item.instructorName}
                                </td>
                              )}
                              <td>
                                <div className="training-inpression-dashboard__table-employee">
                                  <div className="training-inpression-dashboard__table-employee-info">
                                    <div className="training-inpression-dashboard__table-employee-name">
                                      {item.employeeName}
                                    </div>
                                    <div className="training-inpression-dashboard__table-employee-code">
                                      {item.employeeCode}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="training-inpression-dashboard__table-schedule">
                                {item.schedule}
                              </td>
                              <td className="text-center">
                                <span
                                  onClick={() =>
                                    handleFeedbackClick(item.feedback30Id)
                                  }
                                  className={`training-inpression-dashboard__rating rating-small ${getRatingClass(item.rating30)}`}
                                  title={`${Feedback} ${By} ${item.feedback30By}`}
                                >
                                  {getRatingIcon(item.rating30)} {item.rating30}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  onClick={() =>
                                    handleFeedbackClick(item.feedback60Id)
                                  }
                                  className={`training-inpression-dashboard__rating rating-small ${getRatingClass(item.rating60)}`}
                                  title={`${Feedback} ${By} ${item.feedback60By}`}
                                >
                                  {getRatingIcon(item.rating60)} {item.rating60}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  onClick={() =>
                                    handleFeedbackClick(item.feedback90Id)
                                  }
                                  className={`training-inpression-dashboard__rating rating-small ${getRatingClass(item.rating90)}`}
                                  title={`${Feedback} ${By} ${item.feedback90By}`}
                                >
                                  {getRatingIcon(item.rating90)} {item.rating90}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const renderContent = () => {
    switch (activeView) {
      case "employee":
        return renderEmployeeView();
      case "program":
        return renderGroupedView(
          "programName",
          "training-inpression-dashboard__group-header--blue"
        );
      case "instructor":
        return renderGroupedView(
          "instructorName",
          "training-inpression-dashboard__group-header--green"
        );
      case "rm":
        return renderGroupedView(
          "rmName",
          "training-inpression-dashboard__group-header--purple"
        );
      default:
        return renderEmployeeView();
    }
  };

  return (
    <div className="training-inpression-dashboard">
      {/* Header */}
      <div className="training-inpression-dashboard__header">
        <div className="training-inpression-dashboard__header-container">
          <div className="training-inpression-dashboard__header-content">
            <div>
              <p className="training-inpression-dashboard__header-subtitle">
                {TrainingImpressionDashboardSubtitle}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="training-inpression-dashboard__nav">
        <div className="training-inpression-dashboard__nav-container">
          <nav className="training-inpression-dashboard__nav-list">
            {viewOptions.map(option => {
              return (
                <button
                  key={option.id}
                  onClick={() => {
                    setActiveView(option.id);
                    setExpandedGroups(new Set());
                  }}
                  className={`training-inpression-dashboard__nav-item ${
                    activeView === option.id
                      ? "training-inpression-dashboard__nav-item--active"
                      : ""
                  }`}
                >
                  {option.icon}
                  {option.label}
                  <span className="training-inpression-dashboard__nav-item-count">
                    {option.count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Filters */}
      <div className="training-inpression-dashboard__filters">
        <div className="training-inpression-dashboard__filters-container">
          <div className="training-inpression-dashboard__filters-content">
            <FormGroup className="mb-0">
              <LabelTooltip
                label={Search}
                tooltipText={Search}
                important={false}
              />
              <div className="training-inpression-dashboard__filters-search">
                <SearchIcon className="track-time-header__search-icon" />
                <Input
                  type="text"
                  placeholder={SearchAllItems}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="track-time-header__search-input"
                />
                {searchTerm && (
                  <ClearIcon
                    className="track-time-header__clear-icon cursor-pointer"
                    onClick={handleClearSearch}
                  />
                )}
              </div>
            </FormGroup>

            <div className="training-inpression-dashboard__filters-date">
              <Row className="g-2">
                <Col md={6}>
                  <FormGroup className="mb-0">
                    <LabelTooltip
                      label={`${Schedule} ${From}`}
                      tooltipText={`${Schedule} ${From}`}
                      important={false}
                    />

                    <DatePicker
                      placeholderText={`${Select} ${Schedule} ${From}`}
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      disabledKeyboardNavigation={true}
                      onKeyDown={(e: React.KeyboardEvent) => e.preventDefault()}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      selected={new Date(dateRange.start)}
                      onChange={(date: Date) => {
                        const newStartDate = date.toISOString().split("T")[0];
                        const endDate = new Date(dateRange.end);
                        const maxEndDate = new Date(date);
                        maxEndDate.setMonth(maxEndDate.getMonth() + 3);
                        const newEndDate =
                          endDate > maxEndDate
                            ? maxEndDate.toISOString().split("T")[0]
                            : dateRange.end;

                        setDateRange({
                          start: newStartDate,
                          end: newEndDate,
                        });
                      }}
                      maxDate={new Date()}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-0">
                    <LabelTooltip
                      label={`${Schedule} ${To}`}
                      tooltipText={`${Schedule} ${To}`}
                      important={false}
                    />

                    <DatePicker
                      placeholderText={`${Select} ${Schedule} ${To}`}
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      disabledKeyboardNavigation={true}
                      onKeyDown={(e: React.KeyboardEvent) => e.preventDefault()}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      selected={new Date(dateRange.end)}
                      onChange={(date: Date) => {
                        const newEndDate = date.toISOString().split("T")[0];
                        const startDate = new Date(dateRange.start);
                        const maxEndDate = new Date(startDate);
                        maxEndDate.setMonth(maxEndDate.getMonth() + 3);
                        if (date <= maxEndDate) {
                          setDateRange(prev => ({
                            ...prev,
                            end: newEndDate,
                          }));
                        } else {
                          showToast(DateRangeError("3"), "error");
                        }
                      }}
                      minDate={new Date(dateRange.start)}
                      maxDate={(() => {
                        const maxDate = new Date(dateRange.start);
                        maxDate.setMonth(maxDate.getMonth() + 3);
                        return maxDate;
                      })()}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="training-inpression-dashboard__content">
        {renderContent()}
      </div>
    </div>
  );
};

export default TrainingDashboard;
